import { Alert, Button, Card, Skeleton } from '@ui/themed';
import { t } from 'i18next';
import { useEnv } from '../../../context/env-context';
import { useHandlers } from '../../../context/page-context';
import { useSyncStatus } from '../../sync/hooks/use-sync-log-status';
import type { SyncLogStatus } from '../../sync/types';

const PRODUCT_SYNC_LINK = '/integrations/products-sync';
const CUSTOMER_SYNC_LINK = '/integrations/customers-sync';

type DataSyncCardProps = {
  title: string;
  viewActionLabel: string;
  link: string;
  status: SyncLogStatus | undefined;
  isLoading: boolean;
  isError: boolean;
};

function DataSyncStatusAlert({
  status,
  isError,
}: {
  status: SyncLogStatus | undefined;
  isError: boolean;
}) {
  if (isError || !status) {
    return (
      <Alert variant="error" headingLevel="h3">
        {t('features:home.components.data_sync_cards.load_error')}
      </Alert>
    );
  }

  if (!status.last_full_sync) {
    return (
      <Alert variant="warning" headingLevel="h3">
        {t('features:home.components.data_sync_cards.in_progress')}
      </Alert>
    );
  }

  return (
    <Alert variant={status.error_count > 0 ? 'warning' : 'success'} headingLevel="h3">
      {t('features:home.components.data_sync_cards.sync_status', {
        count: status.error_count,
      })}
    </Alert>
  );
}

function DataSyncCard({
  title,
  viewActionLabel,
  link,
  status,
  isLoading,
  isError,
}: DataSyncCardProps) {
  const { navigationHandler } = useHandlers();

  return (
    <Card showBorder variant="base">
      <Card.Header headingLevel="h3" icon="refresh">
        {title}
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <Skeleton />
        ) : (
          <>
            <DataSyncStatusAlert status={status} isError={isError} />
            <span>
              <Button
                variant="secondary"
                onClick={() => navigationHandler.navigate(link, { openInDashboard: true })}
              >
                {viewActionLabel}
              </Button>
            </span>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export function DataSyncCards() {
  const { connected_platform = '' } = useEnv();

  const {
    data: productStatus,
    isError: isProductError,
    isLoading: isLoadingProduct,
  } = useSyncStatus({
    integration: connected_platform,
    dataType: 'products',
  });
  const {
    data: customerStatus,
    isError: isCustomerError,
    isLoading: isLoadingCustomer,
  } = useSyncStatus({
    integration: connected_platform,
    dataType: 'customers',
  });

  return (
    <Card>
      <Card.Header headingLevel="h2">
        {t('features:home.components.data_sync_cards.header')}
      </Card.Header>
      <Card.Body>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DataSyncCard
            title={t('features:home.components.data_sync_cards.product_sync.title')}
            viewActionLabel={t('features:home.components.data_sync_cards.product_sync.view_action')}
            link={PRODUCT_SYNC_LINK}
            status={productStatus}
            isError={isProductError}
            isLoading={isLoadingProduct}
          />
          <DataSyncCard
            title={t('features:home.components.data_sync_cards.customer_sync.title')}
            viewActionLabel={t(
              'features:home.components.data_sync_cards.customer_sync.view_action',
            )}
            link={CUSTOMER_SYNC_LINK}
            status={customerStatus}
            isError={isCustomerError}
            isLoading={isLoadingCustomer}
          />
        </div>
      </Card.Body>
    </Card>
  );
}
